import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

// SmartGrid Imports
import {
  SmartGridComponent,
  ColDef,
  GridConfig,
  PageChangedEvent,
  SortChangedEvent,
  GridReadyEvent,
} from 'app/shared/components/smart-grid';
import { RouteUserCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/route-user-cell-renderer/route-user-cell-renderer.component';
import { DateHourCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/date-hour-cell-renderer/date-hour-cell-renderer.component';
import { CustomerCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/customer-cell-renderer/customer-cell-renderer.component';
import { MaterialCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/material-cell-renderer/material-cell-renderer.component';
import { QuantityCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/quantity-cell-renderer/quantity-cell-renderer.component';
import { CurrencyCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/currency-cell-renderer/currency-cell-renderer.component';
import { ActionsCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/actions-cell-renderer/actions-cell-renderer.component';
import { SerialNavigationCellRendererComponent } from 'app/modules/components/reports/exchange/request-change/cell-renderers/serial-navigation-cell-renderer.component';

import { OrderDetailAttributes, SubRouteAttributes, VisitAttributes, WarehouseAttributes } from '@albatrosdeveloper/ave-models-package';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonToggleModule, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { IPaginationResult } from "app/common/interfaces";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { ExportDialogComponent } from "app/layout/export-overlay/dialog/export-dialog.component";
import { ExportOptions } from "app/layout/export-overlay/tasks/core/export-task.interfaces";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { CompanyService } from "app/modules/components/configuration/company/company.service.service";
import { WarehouseService } from "app/modules/components/configuration/warehouse/warehouse.service";
import { BusinessPartnersFiltersComponent } from "app/modules/components/filters/business-partners-filters/business-partners-filters.component";
import { ConfigurationFiltersComponent } from "app/modules/components/filters/configuration-filters/configuration-filters.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { DocumentsFiltersComponent } from "app/modules/components/filters/documents-filters/documents-filters.component";
import { MaterialsManagementFiltersComponent } from "app/modules/components/filters/materials-management-filters/materials-management-filters.component";
import { RoutesFiltersComponent } from "app/modules/components/filters/routes-filters/routes-filters.component";
import { StatusFiltersComponent } from "app/modules/components/filters/status-filters/status-filters.component";
import { SubRouteService } from "app/modules/components/routing/sub-routes/sub-route.service";
import { DocumentFlowButtonComponent } from "app/modules/shared/document-flow-button/document-flow-button.component";
import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";
import { DecimalFormatPipe } from "app/pipes/decimal-company.pipe";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { UserRouteDisplayComponent } from "app/shared/components/user-route-display/user-route-display.component";
import { getCode } from "app/utils/method";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { debounceTime, distinctUntilChanged, Subscription } from "rxjs";
import {
  ORDER_CHANGE_EXPORT_ID,
  ORDER_CHANGE_TITLE,
} from "../order-customer/exports/order-customer.constants";
import { OrderCustomerExport } from "../order-customer/exports/order-customer.export";
import { OrderCustomerService } from "../order-customer/order-customer.service";
import { OrderStatusERPEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/order-status-erp.enum';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/status.enum';
import { BusinessPartnerCellRendererComponent } from 'app/modules/components/monitor/monitor-routes/cell-renderers/business-partner-cell-renderer.component';
import { TripCellRendererComponent } from '../../visits/visit-report/cell-renderers/trip-cell-renderer.component';
import { Router } from '@angular/router';

@Component({
  selector: "app-change-product",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    SmartGridComponent, // Added for SmartGrid migration
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
    FormatOtherDateHourPipe,
    FilterComponent,
    DecimalFormatPipe,
    DocumentFlowButtonComponent,
    MatMenuModule,
    DatesFiltersComponent,
    FilterSectionComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    MatButtonToggleModule,
    MatButtonToggleGroup,
    MatPseudoCheckboxModule,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    UserRouteDisplayComponent,
    HoverMenuDirective,
    CompanyCurrencyPipe,
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    OrderCustomerExport,
    DecimalPipe,
  ],
  templateUrl: "./change-product.component.html",
  styleUrl: "./change-product.component.scss",
})
export class ChangeProductComponent implements OnInit, OnDestroy {
  getCode = getCode;
  StatusEnum = StatusEnum;
  OrderStatusERPEnum = OrderStatusERPEnum;
  DocumentTypeEnum = DocumentTypeEnum;
  type = DocumentTypeEnum.CAMBIO_DE_PRODUCTO; //solicitud de cambio de producto

  displayedColumns: string[] = [
    "subRoute",
    "serials",
    "date",
    "shipToExternalKeyCode",
    "materialName",
    "return",
    "soldQuantity",
    //"baseUnitOfMeasure",
    "unitPrice",
    "subTotal",
    "total",
    "actions",
  ];

  dataSource = new MatTableDataSource<OrderDetailAttributes>([]);

  // SmartGrid properties
  data: any[] = [];
  loading: boolean = false;

  totalItems: number = 0;
  searchControl = new FormControl("");
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof OrderDetailAttributes = "_id";
  sortDirection: "ASC" | "DESC" = "DESC";

  filter = {
    dateini: new Date(),
    dateend: new Date(),
    warehouseId: null,
    routes: [],
  };

  range = new FormGroup({
    start: new FormControl<Date | null>(new Date()),
    end: new FormControl<Date | null>(new Date()),
  });

  warehouseList: WarehouseAttributes[] = [];
  warehouseFilterList: WarehouseAttributes[] = [];
  searchWarehouse = "";

  subRouteList: SubRouteAttributes[] = [];
  listFilterSubRouteList: SubRouteAttributes[] = [];
  searchSubRoute = "";

  UMPActive: boolean;

  //BUSCADOR
  searchSubscription: Subscription;

  form: FormGroup;

  //Para filtros
  selectedStatuses: number[] = [];

  // Estados ERP
  selectedERPStatuses: number[] = [];

  selectedCustomerType: string[] = [];

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  // SmartGrid Configuration
  columnDefs: ColDef<any>[] = [];
  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "_id" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  constructor(
    private orderCustomer: OrderCustomerService,
    private readonly warehouseService: WarehouseService,
    private readonly subRouteService: SubRouteService,
    private readonly companyService: CompanyService,
    private readonly searchService: SearchService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private orderCustomerExport: OrderCustomerExport,
    private fb: FormBuilder,
    private readonly router: Router
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      startTrip: [],
      endTrip: [],
      companyId: [],
      salesOfficeId: [],
      documentClassId: [],
      subRouteId: [],
      assignedUserId: [],
      payingBusinessPartnerId: [],
      receivingBusinessPartnerId: [],
      warehouseId: [],
      end_startTrip: [],
      end_endTrip: [],
      soldToControl: [],
      searchReceivingBusinessPartner: [],
      searchAssignedUser: [],
      materialGroupId: [[]],
      materialId: [],
    });
  }

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  ngOnInit(): void {
    this.initializeColumnDefs();
  }

  private initializeColumnDefs(): void {
    this.columnDefs = [
      {
        field: "subRoute.code" as any,
        headerName: "Ruta - Usuario",
        minWidth: 300,
        sortable: true,
        pinned: "left",
        cellRenderer: RouteUserCellRendererComponent,
      },          
      {
        field: 'trip.id',
        headerName: 'Viaje',
        minWidth: 150,
        sortable: true,
        cellRenderer: TripCellRendererComponent,
        cellRendererParams: {
          onTripClick: (data: any) => this.goToTravel(data)
        }
      },
      {
        field: "serials",
        headerName: "Nro. Doc.",
        minWidth: 160,
        sortable: true,
        cellRenderer: SerialNavigationCellRendererComponent,
        cellRendererParams: {
          documentType: this.type,
        },
      },
      {
        field: "date",
        headerName: "Fecha",
        minWidth: 140,
        sortable: true,
        cellRenderer: DateHourCellRendererComponent,
      },
      {
        field: "soldTo.name",
        headerName: "Pagador/Receptor",
        cellRenderer: BusinessPartnerCellRendererComponent,
        autoSize: true,
        minWidth: 250, // Much wider for code + long name
        sortable: true,
        filterOptions: {
          // Display customer name in filter dropdown instead of code
          // Note: Requires shipToName field to be present in row data
          valueExtractor: (item: any) => item?.soldTo?.name,
          customFilterFn(rowData, filterValue) {
            const rowCustomer = rowData?.soldTo?.name;
            if (!rowCustomer) return !filterValue;

            return rowCustomer === filterValue;
          },
        },
      },
      {
        field: "materialName.name" as any,
        headerName: "Material",
        minWidth: 200,
        sortable: true,
        cellRenderer: MaterialCellRendererComponent,
      },
      {
        field: "returnType.name" as any,
        headerName: "Motivo de Cambio",
        minWidth: 180,
        sortable: true,
        valueFormatter: (value: any, data: any) =>
          data?.returnType?.name || "-",
      },
      {
        field: "soldQuantity",
        headerName: "Cantidad (U.M)",
        minWidth: 180,
        sortable: true,
        cellRenderer: QuantityCellRendererComponent,
        cellRendererParams: {
          getUMPActive: () => this.UMPActive, // Arrow function para valor dinámico
        },
      },
      {
        field: "unitPrice",
        headerName: "Precio",
        minWidth: 120,
        sortable: true,
        cellClass: "text-right",
        cellRenderer: CurrencyCellRendererComponent,
      },
      {
        field: "subTotal",
        headerName: "Subtotal",
        minWidth: 120,
        sortable: true,
        cellClass: "text-right",
        cellRenderer: CurrencyCellRendererComponent,
      },
      {
        field: "total",
        headerName: "Total",
        minWidth: 120,
        sortable: true,
        cellClass: "text-right",
        cellRenderer: CurrencyCellRendererComponent,
      },
      {
        colId: "actions",
        headerName: "",
        minWidth: 80,
        maxWidth: 80,
        sortable: false,
        pinned: "right",
        cellRenderer: ActionsCellRendererComponent,
      },
    ];
  }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.fetchCompanies();
    // this.fetchAllWarehouses();
    this.onFilter();

    this.searchControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((filterValue: string) => {
        this.pageIndex = 1;
        this.search = filterValue;
        this.onFilter();
      });
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$.subscribe(
      (term) => {
        this.pageIndex = 1;
        this.search = term;
        this.onFilter();
      }
    );
  }

  onFilter() {
    this.range.valueChanges.subscribe((rangeValues) => {
      this.filter.dateini = rangeValues.start || this.filter.dateini;
      this.filter.dateend = rangeValues.end || this.filter.dateend;
    });

    this.getListReport();
  }

  goToTravel(element: VisitAttributes) {
        if (element.trip?.id) {
          const queryParams = { tripId: element.trip.id };
          const urlTree = this.router.createUrlTree(['monitor', 'principal-screen'], { queryParams });
          const absoluteUrl = window.location.origin + this.router.serializeUrl(urlTree);
          window.open(absoluteUrl, '_blank');
        }
      }

  onClearFilter() {
    this.filter = {
      dateini: new Date(),
      dateend: new Date(),
      warehouseId: null,
      routes: [],
    };
    this.range.reset({
      start: new Date(),
      end: new Date(),
    });
    const today = new Date();
    this.form.reset({ start: today, end: today });
    this.onFilter();
  }

  applyFilter(filterValue: string) {
    this.pageIndex = 1;
    this.search = filterValue;
    this.getListReport();
  }

  // SmartGrid Event Handlers
  onPageChanged(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.getListReport();
  }

  onSortChanged(event: SortChangedEvent<any>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as keyof OrderDetailAttributes;
      this.sortDirection = sortState.direction === "asc" ? "ASC" : "DESC";
    } else {
      this.sortActive = "_id";
      this.sortDirection = "DESC";
    }
    this.getListReport();
  }

  onGridReady(event: GridReadyEvent<any>): void {
    // Can be used for additional initialization if needed
  }

  // Keep old methods for backward compatibility
  changeSort(e) {
    this.sortActive = e.active;
    this.sortDirection = e.direction?.toUpperCase() || "ASC";
    this.getListReport();
  }

  changePage(e) {
    this.pageIndex = e.pageIndex + 1;
    this.pageSize = e.pageSize;
    this.getListReport();
  }

  getListReport() {
    this.loading = true; // Start loading
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    const adjustedStart = new Date(this.filter.dateini);
    adjustedStart.setHours(0, 0, 0, 0); // 00:00:00.000

    const adjustedEnd = new Date(this.filter.dateend);
    adjustedEnd.setHours(23, 59, 59, 999); // 23:59:59.999

    // let filter = {
    //   type: this.type, //Cambio,
    //   start: adjustedStart.toISOString(), // Convertir a UTC
    //   end: adjustedEnd.toISOString(),     // Convertir a UTC
    // };

    let filter = {
      type: this.type,
      startDate: this.form.get("start")?.value
        ? new Date(
            new Date(this.form.get("start")?.value).setHours(0, 0, 0, 0)
          ).toISOString()
        : null,
      endDate: this.form.get("end")?.value
        ? new Date(
            new Date(this.form.get("end")?.value).setHours(23, 59, 59, 999)
          ).toISOString()
        : null,
      startTrip: this.form.get("startTrip")?.value
        ? new Date(
            new Date(this.form.get("startTrip")?.value).setHours(0, 0, 0, 0)
          ).toISOString()
        : null,
      endTrip: this.form.get("endTrip")?.value
        ? new Date(
            new Date(this.form.get("endTrip")?.value).setHours(23, 59, 59, 999)
          ).toISOString()
        : null,
      end_startTrip: this.form.get("end_startTrip")?.value
        ? new Date(
            new Date(this.form.get("end_startTrip")?.value).setHours(0, 0, 0, 0)
          ).toISOString()
        : null,
      end_endTrip: this.form.get("end_endTrip")?.value
        ? new Date(
            new Date(this.form.get("end_endTrip")?.value).setHours(
              23,
              59,
              59,
              999
            )
          ).toISOString()
        : null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value, //
      documentClass: this.form.get("documentClassId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //
      soldTo: this.form.get("payingBusinessPartnerId")?.value,
      shipTo: this.form.get("receivingBusinessPartnerId")?.value,
      seller: this.form.get("assignedUserId")?.value,
      status: this.selectedStatuses ? this.selectedStatuses : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId: this.form.get("materialId").value,
      ordersToFilter: null,
    };

    this.orderCustomer
      .findAllWithCount(
        options,
        // this.filter.warehouseId ? [this.filter.warehouseId] : [],
        // this.filter.routes,
        filter
      )
      .subscribe({
        next: (res: IPaginationResult<OrderDetailAttributes>) => {
          this.dataSource.data = res?.data?.[0]?.data ?? []; // Keep for backward compatibility
          this.data = res?.data?.[0]?.data ?? []; // For SmartGrid
          this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
          this.loading = false; // Stop loading
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al obtener reporte",
            "Cerrar",
            { duration: 5000 }
          );
          this.loading = false; // Stop loading on error
        },
      });
  }

  private getSortField(): keyof OrderDetailAttributes {

    return this.sortActive;
  }

  //filter
  private fetchCompanies(): void {
    this.companyService.findToComboBox().subscribe((res) => {
      if (res.data.length === 1) {
        this.UMPActive = res.data[0].UMPActive;
      }
    });
  }

  private fetchAllWarehouses(): void {
    this.warehouseService.findToComboBox().subscribe({
      next: (res) => {
        this.warehouseList = res.data;
        this.warehouseFilterList = this.warehouseList.slice();
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error fetching warehouses",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  changueOptionsWarehouse(event) {
    if (!this.warehouseList) {
      return;
    }
    let search = event;
    if (!search) {
      this.warehouseFilterList = this.warehouseList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.warehouseFilterList = this.warehouseList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  changeWarehouse(event): void {
    this.subRouteList = [];
    this.listFilterSubRouteList = [];
    this.getAllRutas();
  }

  private getAllRutas(): void {
    this.subRouteService
      .findToWarehouse(this.filter.warehouseId ? [this.filter.warehouseId] : [])
      .subscribe({
        next: (res) => {
          this.subRouteList = res.data;
          this.listFilterSubRouteList = this.subRouteList.slice();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error fetching route",
            "Cerrar",
            { duration: 5000 }
          );
        },
      });
  }

  changueOptionsSubRoute(event) {
    if (!this.subRouteList) {
      return;
    }
    let search = event;
    if (!search) {
      this.listFilterSubRouteList = this.subRouteList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.listFilterSubRouteList = this.subRouteList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  onExportButtonPressed() {
    if (!this.orderCustomerExport.canStartTask()) return;
    const dialogRef = this.matDialog.open(ExportDialogComponent, {
      data: { title: ORDER_CHANGE_TITLE },
    });
    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.orderCustomerExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
          ORDER_CHANGE_EXPORT_ID,
          ORDER_CHANGE_TITLE
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
